/**
 * REST client. Access token lives in memory only; the refresh token is an httpOnly SameSite=strict cookie
 * (never readable by JS). Expired access tokens are refreshed transparently once per burst of requests.
 */
import type { AuthTokens } from '@cardhub/protocol';
import { deviceId } from '../lib/device';
import { getStoredRefreshToken, isNative, platform, storeRefreshToken } from '../native/platform';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
let accessToken: string | null = null;
let refreshing: Promise<AuthTokens | null> | null = null;
let refreshUnreachable = false;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(t: string | null): void {
  accessToken = t;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function onSessionExpired(fn: () => void): void {
  onSessionLost = fn;
}
/** The server definitively rejected the saved login (not just unreachable): ask the player to sign in again. */
export function notifySessionLost(): void {
  onSessionLost?.();
}

function headers(json: boolean): Record<string, string> {
  const h: Record<string, string> = {
    'x-device-id': deviceId(),
    'x-platform': platform,
    'x-requested-with': 'cardhub',
  };
  if (json) h['content-type'] = 'application/json';
  if (accessToken) h.authorization = `Bearer ${accessToken}`;
  return h;
}

/** True when the last refresh failed because the server could not be reached (offline), not because the session is invalid. */
export function refreshWasUnreachable(): boolean {
  return refreshUnreachable;
}

/** Refreshes the session using the httpOnly cookie. Returns null if there is no valid session. */
export function refreshSession(): Promise<AuthTokens | null> {
  if (!refreshing) {
    refreshing = (async () => {
      refreshUnreachable = false;
      try {
        // Native apps can't rely on cookies across origins: the refresh token lives in app storage.
        const stored = isNative ? await getStoredRefreshToken() : null;
        if (isNative && !stored) return null;
        const res = await fetch(`${BASE}/api/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: headers(true),
          body: JSON.stringify(stored ? { refreshToken: stored } : {}),
          signal: AbortSignal.timeout(10_000),
        });
        if (!res.ok) {
          if (res.status >= 500) refreshUnreachable = true;
          if (isNative && res.status === 401) await storeRefreshToken(null);
          return null;
        }
        const body = (await res.json()) as { tokens: AuthTokens | null };
        if (!body.tokens) return null;
        accessToken = body.tokens.accessToken;
        await storeRefreshToken(body.tokens.refreshToken);
        return body.tokens;
      } catch {
        refreshUnreachable = true;
        return null;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })();
  }
  return refreshing;
}

async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: headers(body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      // A half-connected network can leave a request pending forever; give up so the app falls back to offline play.
      signal: AbortSignal.timeout(20_000),
    });
  } catch {
    throw new ApiError('NETWORK', "Can't reach the server. Check your connection.", 0);
  }
  if (res.status === 401 && retry && accessToken && !path.startsWith('/api/v1/auth/')) {
    const t = await refreshSession();
    if (t) return request<T>(method, path, body, false);
    accessToken = null;
    onSessionLost?.();
  }
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = (data as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    throw new ApiError(err?.code ?? 'HTTP_' + res.status, err?.message ?? res.statusText, res.status, err?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}
